import { useMemo } from 'react'
import { usePathname, useSearchParams } from '@/lib/router'
import { type MenuKey, menus } from '@/lib/constants'
import type { Contact } from '@/types'

type MenuItem = (typeof menus)[MenuKey]['items'][number]

type RouteContext = {
  activeMenu: MenuKey
  activeItem: MenuItem
  activeConversationWxid: string
  groups: Contact[]
  friends: Contact[]
}

function findRoute(pathname: string): { menuKey: MenuKey; item: MenuItem } | null {
  for (const [menuKey, menu] of Object.entries(menus) as Array<[MenuKey, (typeof menus)[MenuKey]]>) {
    const item = menu.items.find((candidate) => candidate.path === pathname)
    if (item) return { menuKey, item }
  }
  return null
}

export function useRouteContext(contacts: Contact[]): RouteContext {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const activeConversationWxid = searchParams.get('conversationWxid') || searchParams.get('wxid') || ''

  const matchedRoute = findRoute(pathname)
  const rawSection = pathname.split('/')[1] || 'dashboard'
  const fallbackMenu: MenuKey = menus[rawSection as MenuKey] ? (rawSection as MenuKey) : 'dashboard'
  const activeMenu = matchedRoute?.menuKey || fallbackMenu
  const activeItem = matchedRoute?.item || menus[activeMenu].items[0]

  // Stable references matter: these flow down to panels whose own useMemo
  // caches (label member counts over the whole address book) would otherwise
  // be thrown away on every inbound message, since App re-renders on unread
  // count changes.
  const groups = useMemo(() => contacts.filter((contact) => contact.wxid.includes('@chatroom')), [contacts])
  const friends = useMemo(() => contacts.filter((contact) => !contact.wxid.includes('@chatroom')), [contacts])

  return { activeMenu, activeItem, activeConversationWxid, groups, friends }
}
